import { Injectable } from '@angular/core';
import { IContextData } from './contexts/common';
import {ENS} from './contexts/ens';
import {MPT} from './contexts/mpt';
import {VEA} from './contexts/vea';
import {EQAO} from './contexts/eqao';
import { environment } from "../../environments/environment";

const availableContexts:IContextData[] = [ // first entry is also the default
  // ENS,
  MPT,
  // EQAO,
  VEA,
  // MEES,
];

export const TEMPLATE_BRAND_NAME = '%BRAND_NAME%';

// API URL override logic
const API_URL_STORAGE_KEY = "apiUrlOverrideV2";
const ALLOWED_API_OVERRIDE_HOSTS = ["vea-api-staging.vretta.com", "vea-api-staging-k8s.vretta.com"];

function getApiUrlOverride() {
  const params = new URLSearchParams(window.location.search);
  const apiParam = params.get("apiUrl");
  if (apiParam !== null) {
    if (apiParam === "") {
      localStorage.removeItem(API_URL_STORAGE_KEY); // clear override
      console.info("API address override cleared by empty query param.");
      return null;
    }
    try {
      const url = new URL(apiParam);
      if (url.protocol === "https:" && ALLOWED_API_OVERRIDE_HOSTS.includes(url.hostname)) {
        localStorage.setItem(API_URL_STORAGE_KEY, apiParam); // store valid override
        console.info("Using API override address from query parameter:", apiParam);
        return apiParam;
      }
    } catch {}
    localStorage.removeItem(API_URL_STORAGE_KEY); // remove invalid
    console.warn("Invalid API override address provided in query parameter:", apiParam);
    return null;
  }
  // Fallback to localStorage
  const stored = localStorage.getItem(API_URL_STORAGE_KEY);
  if (stored) {
    try {
      const url = new URL(stored);
      if (url.protocol === "https:" && ALLOWED_API_OVERRIDE_HOSTS.includes(url.hostname)) {
        console.info("Using API override address from localStorage:", stored);
        return stored;
      }
    } catch {}
    localStorage.removeItem(API_URL_STORAGE_KEY); // remove invalid
    console.warn("Invalid API override address in localStorage, removed:", stored);
  }
  return null;
}

const apiUrlOverride = getApiUrlOverride();

@Injectable({
  providedIn: 'root'
})
export class WhitelabelService {

  context:string;
  contextData:IContextData;
  defaultWhitelabelContext:string;

  currentHomepageRoute:string;
  currentBrandName:string;
  currentLogo:string;

  constructor() {
    const hostMap:{}[] = []
    const contextRef = this.registerContexts(availableContexts);
    // const storedWhitelabel = localStorage.getItem('defaultWhitelabel'); // need to check if the data is defined before using
    this.context = contextRef.hostRef.get(window.location.host) || contextRef.defaultContextId;
    this.contextData = contextRef.data.get(this.context);
    this.currentHomepageRoute = this.contextData.homepageRoute;
    this.currentBrandName = this.contextData.brandName;
    this.currentLogo = this.contextData.logo;
    this.ensureDefaultContext();
  }

  registerContexts(contexts:IContextData[]){
    type ContextId = string;
    const hostRef:Map<ContextId, string> = new Map();
    const data:Map<ContextId, IContextData> = new Map();
    const defaultContextId = contexts[0].id;
    contexts.forEach(contextData => {
      if (data.has(contextData.id)){
        console.warn('Duplicate WL context definition.', contextData.id);
      }
      else{
        data.set(contextData.id, contextData);
        contextData.hosts.forEach(host => {
          if (hostRef.has(host)){
            console.warn('Duplicate host WL definition.', host, contextData.id, hostRef.get(host));
          }
          else{
            hostRef.set(host, contextData.id);
          }
        });
      }
    });
    return {data, hostRef, defaultContextId};
  }

  ensureDefaultContext(){
    const context = this.contextData;
    if (!context){
      console.error('using undefined context');
    }
    context.siteText = this.contextData.siteText || {};
    context.siteFlags = this.contextData.siteFlags || {};
    context.footer = this.contextData.footer || [];
    context.siteText = this.contextData.siteText || {};
    const siteText = this.contextData.siteText;
    if (!siteText['SITE_LINK']){
      siteText['SITE_LINK'] = window.location.host;
    }
  }

  onReady( callback ){
    callback(); // need to delay this if config is loaded over a file
  }

  updateTemplateContent(str:string){
    if (str){
      return str.split(TEMPLATE_BRAND_NAME).join(this.currentBrandName);
    }
    return str;
  }

  getFrontendUrl() {
    if (!environment.production) {
      const hostname = window.location.hostname;

      // Staging environment
      if (hostname === 'deployments-staging.vretta.com') {
        const milestone = window.location.pathname.split('/')[1];
        if (milestone) return `https://deployments-staging.vretta.com/${milestone}`;
      }

      // K8s Staging environment
      if (hostname === 'deployments-staging-k8s.vretta.com') {
        const milestone = window.location.pathname.split('/')[1];
        if (milestone) return `https://deployments-staging-k8s.vretta.com/${milestone}`;
      }
    }

    return window.location.origin;
  }

  getApiAddress() {
    if (!environment.production && apiUrlOverride) {
      return apiUrlOverride;
    }
    if (!environment.production) {
      const hostname = window.location.hostname;
      if (hostname === 'deployments-staging.vretta.com') {
        const milestone = window.location.pathname.split('/')[1];
        if (milestone) return `https://vea-api-staging.vretta.com/${milestone}`;
      }
      if (hostname === 'deployments-staging-k8s.vretta.com') {
        const milestone = window.location.pathname.split('/')[1];
        if (milestone) return `https://vea-api-staging-k8s.vretta.com/${milestone}`;
      }
    }
    return this.contextData.apiAddress(window.location.hostname);
  }
  getApiAddressForWS(){
    // return 'https://1ee7-108-162-129-138.ngrok-free.app' // todo: make this conditional on localhost through whitelabel
    return this.contextData.apiAddress(window.location.hostname);
  }
  setMPTctrlID(){this.contextData.setMPTctrlID(window.location.hostname)}
  getHomepageRoute(){ return this.currentHomepageRoute; }
  getSiteFlag(slug:string){ return this.contextData.siteFlags[slug]; }
  getSiteText(slug:string, defaultText:string=''){ return this.contextData.siteText[slug] || defaultText; }
  getFooter(){ return this.contextData.footer; }
  getDefaultCurriculumId(){ return ''; }
  
  IS_EMBEDDED_PROCT_ALLOWED = () => this.getSiteFlag('IS_EMBEDDED_PROCT_ALLOWED');
  IS_TT_DASH_SUMBMISSION_SIMPLIFIED = () => this.getSiteFlag('IS_TT_DASH_SUMBMISSION_SIMPLIFIED');
}
